import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, Card, CardContent, CircularProgress,
  Alert, Chip, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Button, Divider
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import BusinessIcon from '@mui/icons-material/Business';
import FactoryIcon from '@mui/icons-material/Factory';
import HubIcon from '@mui/icons-material/Hub';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import RecyclingIcon from '@mui/icons-material/Recycling';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import { nodeNetworkService } from '../services/nodeNetworkService';
import { v5Service } from '../services/v5Service';
import {
  NODE_TYPE_COLORS, ISSUE_SEVERITY_COLORS,
  PACT_STATUS_COLORS, formatEmissions
} from '../constants/nodeTypes';

function Dashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [v5, setV5] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllIssues, setShowAllIssues] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashboardResult, circularityResult, waterResult] = await Promise.allSettled([
        nodeNetworkService.getDashboard(),
        v5Service.getCircularitySummary(),
        v5Service.getWaterSummary(),
      ]);
      if (dashboardResult.status === 'rejected') {
        throw dashboardResult.reason;
      }
      setDashboard(dashboardResult.value);
      // v5 data is optional — the dashboard still renders when OFP v5 is empty.
      if (circularityResult.status === 'fulfilled' && waterResult.status === 'fulfilled') {
        setV5({ circularity: circularityResult.value, water: waterResult.value });
      }
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!dashboard) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
        <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">No dashboard data available</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The API returned an empty response. Please try again later.
        </Typography>
      </Box>
    );
  }

  const {
    aggregate_totals: totals = {
      total_networks: 0,
      total_nodes: 0,
      total_ledgers: 0,
      total_emissions_kg_co2e: 0,
      total_unallocated_kg_co2e: 0,
      compliance_pct: 0,
      ofp_total_organizations: 0,
      ofp_total_facilities: 0,
    },
    node_health: nodeHealth = [],
    ofp_summary: ofpSummary = [],
    issues = []
  } = dashboard || {};

  // ── KPI Bar ──────────────────────────────────────────────
  const kpiCards = [
    { title: 'Networks', value: totals.total_networks ?? 0, icon: <HubIcon fontSize="large" />, color: '#1976d2' },
    { title: 'Nodes', value: totals.total_nodes ?? 0, icon: <DeviceHubIcon fontSize="large" />, color: '#7b1fa2' },
    { title: 'E-Ledgers', value: totals.total_ledgers ?? 0, icon: <AccountBalanceIcon fontSize="large" />, color: '#0288d1' },
    { title: 'Total Emissions', value: formatEmissions(totals.total_emissions_kg_co2e ?? 0), icon: <TrendingUpIcon fontSize="large" />, color: '#d32f2f' },
    { title: 'Unallocated', value: formatEmissions(totals.total_unallocated_kg_co2e ?? 0), icon: <WarningIcon fontSize="large" />, color: (totals.total_unallocated_kg_co2e ?? 0) > 0 ? '#ed6c02' : '#2e7d32' },
    { title: 'Compliance', value: `${totals.compliance_pct ?? 0}%`, icon: <CheckCircleIcon fontSize="large" />, color: (totals.compliance_pct ?? 0) >= 90 ? '#2e7d32' : (totals.compliance_pct ?? 0) >= 50 ? '#ed6c02' : '#d32f2f' },
    { title: 'OFP Orgs', value: totals.ofp_total_organizations ?? 0, icon: <BusinessIcon fontSize="large" />, color: '#00796b' },
    { title: 'OFP Facilities', value: totals.ofp_total_facilities ?? 0, icon: <FactoryIcon fontSize="large" />, color: '#00838f' },
  ];

  // ── Issue severity icon ──────────────────────────────────
  const severityIcon = (severity) => {
    switch (severity) {
      case 'high': return <ErrorIcon sx={{ color: ISSUE_SEVERITY_COLORS.high, fontSize: 20 }} />;
      case 'medium': return <WarningIcon sx={{ color: ISSUE_SEVERITY_COLORS.medium, fontSize: 20 }} />;
      default: return <InfoIcon sx={{ color: ISSUE_SEVERITY_COLORS.low, fontSize: 20 }} />;
    }
  };

  const visibleIssues = showAllIssues ? issues : issues.slice(0, 5);

  // ── Emission treemap data ────────────────────────────────
  const treemapData = nodeHealth.map(n => ({
    name: n.node_name,
    value: n.total_debits,
    nodeType: n.node_type,
    ofpLinked: n.ofp_linked,
    itemStyle: {
      color: NODE_TYPE_COLORS[n.node_type] || '#90a4ae',
      borderColor: n.ofp_linked ? '#4caf50' : '#bdbdbd',
      borderWidth: n.ofp_linked ? 3 : 1,
      borderType: n.ofp_linked ? 'solid' : 'dashed'
    },
    children: (n.ledger_count ?? 0) > 0 ? [] : undefined
  }));

  const treemapOption = {
    tooltip: {
      formatter: (info) => {
        const d = info.data;
        return `<b>${d.name}</b><br/>Type: ${d.nodeType}<br/>Emissions: ${formatEmissions(d.value)}<br/>OFP: ${d.ofpLinked ? 'Linked' : 'Not linked'}`;
      }
    },
    series: [{
      type: 'treemap',
      data: treemapData,
      roam: false,
      nodeClick: false,
      breadcrumb: { show: false },
      label: {
        show: true,
        formatter: (params) => `${params.name}\n${formatEmissions(params.value)}`,
        fontSize: 11
      },
      itemStyle: {
        borderColor: '#fff',
        borderWidth: 2,
        gapWidth: 2
      },
      levels: [{
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 2,
          gapWidth: 2
        }
      }]
    }]
  };

  // ── v5 circularity / water aggregates ───────────────────
  const v5Orgs = v5?.circularity ?? [];
  const v5Water = v5?.water ?? [];
  const v5Mean = (key) => {
    const vals = v5Orgs.map((o) => Number(o[key] || 0)).filter((v) => v > 0);
    return vals.length ? vals.reduce((a, v) => a + v, 0) / vals.length : 0;
  };
  const v5Withdrawal = v5Water.reduce((a, o) => a + Number(o.withdrawal_m3 || 0), 0);
  const v5Stressed = v5Water.reduce((a, o) => a + Number(o.stressed_facilities || 0), 0);

  const v5Cards = [
    { title: 'Avg circular inflow (CR_IN)', value: `${v5Mean('avg_cr_in').toFixed(1)}%`, icon: <RecyclingIcon fontSize="large" />, color: '#2e7d32' },
    { title: 'Avg recycling rate (RR)', value: `${v5Mean('avg_rr').toFixed(1)}%`, icon: <RecyclingIcon fontSize="large" />, color: '#00897b' },
    { title: 'Water withdrawal', value: `${Math.round(v5Withdrawal).toLocaleString()} m³`, icon: <WaterDropIcon fontSize="large" />, color: '#1976d2' },
    { title: 'Stressed-site facilities', value: v5Stressed, icon: <WarningIcon fontSize="large" />, color: v5Stressed > 0 ? '#d32f2f' : '#2e7d32' },
  ];

  const v5FlowChartOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: (v) => `${Number(v).toLocaleString()} kg`,
    },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 8, right: 8, top: 24, bottom: 40, containLabel: true },
    xAxis: {
      type: 'category',
      data: v5Orgs.map((o) => o.organization_name),
      axisLabel: { fontSize: 10, interval: 0, rotate: 18 },
    },
    yAxis: { type: 'value', axisLabel: { formatter: (v) => `${Math.round(v / 1000)}t` } },
    series: [
      { name: 'Circular in', type: 'bar', stack: 'input', itemStyle: { color: '#2e7d32' }, data: v5Orgs.map((o) => Number(o.circular_in_kg || 0)) },
      { name: 'Linear in', type: 'bar', stack: 'input', itemStyle: { color: '#a5d6a7' }, data: v5Orgs.map((o) => Number(o.input_kg || 0) - Number(o.circular_in_kg || 0)) },
      { name: 'Circular out', type: 'bar', stack: 'output', itemStyle: { color: '#1565c0' }, data: v5Orgs.map((o) => Number(o.circular_out_kg || 0)) },
      { name: 'Linear out', type: 'bar', stack: 'output', itemStyle: { color: '#90caf9' }, data: v5Orgs.map((o) => Number(o.output_kg || 0) - Number(o.circular_out_kg || 0)) },
    ],
  };

  const v5WaterChartOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: (v) => `${Number(v).toLocaleString()} m³`,
    },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 8, right: 8, top: 24, bottom: 40, containLabel: true },
    xAxis: {
      type: 'category',
      data: v5Water.map((o) => o.organization_name),
      axisLabel: { fontSize: 10, interval: 0, rotate: 18 },
    },
    yAxis: { type: 'value' },
    series: [
      { name: 'Withdrawal', type: 'bar', itemStyle: { color: '#1976d2' }, data: v5Water.map((o) => Number(o.withdrawal_m3 || 0)) },
      { name: 'Discharge', type: 'bar', itemStyle: { color: '#00838f' }, data: v5Water.map((o) => Number(o.discharge_m3 || 0)) },
      { name: 'Recycled', type: 'bar', itemStyle: { color: '#2e7d32' }, data: v5Water.map((o) => Number(o.recycle_m3 || 0)) },
    ],
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Network-centric carbon accounting with Open Footprint context
      </Typography>

      {/* ── KPI Bar ──────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card elevation={2}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {card.title}
                    </Typography>
                    <Typography variant="h6" sx={{ color: card.color, fontWeight: 600 }}>
                      {card.value}
                    </Typography>
                  </Box>
                  <Box sx={{ color: card.color, opacity: 0.7 }}>
                    {card.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Two-column: Node Health + Issues ─────────────── */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Node Health Matrix
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Node</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="center">Ledgers</TableCell>
                  <TableCell align="center">Identity</TableCell>
                  <TableCell align="center">PACT</TableCell>
                  <TableCell>OFP Facility</TableCell>
                  <TableCell align="right">Emissions</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nodeHealth.map((n) => (
                  <TableRow key={n.node_id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => navigate(`/node-network/nodes/${n.node_id}`)}
                      >
                        {n.node_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={n.node_type}
                        size="small"
                        sx={{ backgroundColor: NODE_TYPE_COLORS[n.node_type], color: '#fff', fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{n.ledger_count}</Typography>
                      {n.active_ledgers > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          ({n.active_ledgers} active)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {n.bound_identity ? (
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} />
                      ) : (
                        <Tooltip title="No bound identity">
                          <WarningIcon sx={{ color: '#ff9800', fontSize: 18 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {n.pact_sync_status ? (
                        <Chip
                          label={n.pact_sync_status}
                          size="small"
                          sx={{
                            backgroundColor: PACT_STATUS_COLORS[n.pact_sync_status] || '#9e9e9e',
                            color: '#fff',
                            fontSize: '0.65rem',
                            height: 20
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">--</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {n.ofp_facility_name ? (
                        <Tooltip title={`${n.ofp_organization_name} / ${n.ofp_boundary_name}`}>
                          <Chip
                            icon={<FactoryIcon sx={{ fontSize: 14 }} />}
                            label={n.ofp_facility_name}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', maxWidth: 160 }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          icon={<LinkOffIcon sx={{ fontSize: 14 }} />}
                          label="Not in OFP"
                          size="small"
                          sx={{ color: '#9e9e9e', fontSize: '0.7rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{formatEmissions(n.total_debits)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ color: n.balance > 0 ? 'warning.main' : 'success.main' }}
                      >
                        {formatEmissions(n.balance)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Issue Alerts
            </Typography>
            {issues.length === 0 ? (
              <Alert severity="success">No issues detected</Alert>
            ) : (
              <Box>
                {visibleIssues.map((issue, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      py: 1,
                      borderBottom: idx < visibleIssues.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    {severityIcon(issue.severity)}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {issue.message}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => {
                          if (issue.action_route.startsWith('/node-network')) {
                            navigate(issue.action_route);
                          } else if (issue.action_route.startsWith('/e-ledgers')) {
                            navigate(issue.action_route);
                          }
                        }}
                        sx={{ mt: 0.5, fontSize: '0.7rem' }}
                      >
                        {issue.action_label}
                      </Button>
                    </Box>
                  </Box>
                ))}
                {issues.length > 5 && (
                  <Button
                    size="small"
                    onClick={() => setShowAllIssues(!showAllIssues)}
                    startIcon={showAllIssues ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mt: 1 }}
                  >
                    {showAllIssues ? 'Show less' : `Show all (${issues.length})`}
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Circularity & Water (v5) ─────────────────────── */}
      {v5Orgs.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RecyclingIcon color="primary" />
              <Typography variant="h6">Circularity &amp; Water (v5)</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<RecyclingIcon />} onClick={() => navigate('/circularity')}>
                Circularity
              </Button>
              <Button size="small" startIcon={<WaterDropIcon />} onClick={() => navigate('/water')}>
                Water
              </Button>
            </Box>
          </Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {v5Cards.map((card, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card elevation={2}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {card.title}
                        </Typography>
                        <Typography variant="h6" sx={{ color: card.color, fontWeight: 600 }}>
                          {card.value}
                        </Typography>
                      </Box>
                      <Box sx={{ color: card.color, opacity: 0.7 }}>
                        {card.icon}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Circular vs linear flows by organization (kg)
              </Typography>
              <ReactECharts option={v5FlowChartOption} style={{ height: 260 }} />
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Water by organization (m³)
              </Typography>
              <ReactECharts option={v5WaterChartOption} style={{ height: 260 }} />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* ── OFP Hierarchy Row ─────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Open Footprint Hierarchy
        </Typography>
        <Grid container spacing={2}>
          {ofpSummary.map((org) => (
            <Grid item xs={12} md={4} key={org.organization_id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <BusinessIcon sx={{ color: '#00796b' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      {org.organization_name}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Boundary: {org.boundary_name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Chip label={`${org.emission_activity_count} activities`} size="small" sx={{ fontSize: '0.7rem' }} />
                    <Chip label={`${org.emission_statement_count} statements`} size="small" sx={{ fontSize: '0.7rem' }} />
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  {(org.facilities || []).map((f) => (
                    <Box
                      key={f.facility_id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.5,
                        px: 1,
                        borderRadius: 1,
                        backgroundColor: f.node_id ? 'action.hover' : 'transparent',
                        mb: 0.5
                      }}
                    >
                      <FactoryIcon sx={{ fontSize: 16, color: f.node_id ? '#4caf50' : '#bdbdbd' }} />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {f.facility_name}
                      </Typography>
                      {f.node_id ? (
                        <Tooltip title={`Linked to: ${f.node_name}`}>
                          <Chip
                            label={f.node_name}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              height: 20,
                              backgroundColor: NODE_TYPE_COLORS[nodeHealth.find(n => n.node_id === f.node_id)?.node_type] || '#e0e0e0',
                              color: '#fff',
                              cursor: 'pointer'
                            }}
                            onClick={() => navigate(`/node-network/nodes/${f.node_id}`)}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          label="Unlinked"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20, color: '#9e9e9e' }}
                        />
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* ── Emission Breakdown Treemap ────────────────────── */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Emission Breakdown by Node
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid #4caf50' }} />
            <Typography variant="caption">OFP Linked</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '1px dashed #bdbdbd' }} />
            <Typography variant="caption">Not OFP Linked</Typography>
          </Box>
        </Box>
        {treemapData.length > 0 ? (
          <ReactECharts option={treemapOption} style={{ height: 300 }} />
        ) : (
          <Typography color="text.secondary">No emission data available</Typography>
        )}
      </Paper>
    </Box>
  );
}

export default Dashboard;
